import React, { useState, useRef } from 'react';
import { Card, CardContent, Typography, Tooltip, Button } from '@mui/material';
import { useStyles } from '../UrbanSportsConnect/ConnectUrbanSports.styles';
import { ContentCopy } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useProvider } from '~/Hooks';
import { Error } from '~/Components';
import { useRequestActiveLocations } from '~/requests/providerService/useRequestActiveLocations';
import { Dots } from '~/UIKit';
import { PRIMARY_BLUE } from '~/Constants/Colors.constants';
import { downloadCSV } from '~/Helpers/CsvHelper';
import { useLocationListWithStatus } from '~/Hooks/useLocationsList';
import { useRequestChannels } from '~/requests/adapterSvc/useRequestChannels';
import { LocationListItem } from './LocationListItemCard';
import { LocationStatus } from '~/Models/DTO/externalParties.model';

interface Location {
  id: string;
  uuid: string;
  providerId?: string;
  name: string;
  street: string;
  city: string;
  zipCode: string;
  status?: LocationStatus | null;
  integrationStatus?: string;
}

export const TokenListCard = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { currentProvider } = useProvider();
  const providerUUID = String(currentProvider?.uuid);
  const [showCopied, setShowCopied] = useState(false);

  const {
    data: locations,
    error: locationsError,
    isLoading: locationsLoading,
  } = useRequestActiveLocations({ providerUUID });

  const { data: locationData } = useLocationListWithStatus({});

  const { data: integrations } = useRequestChannels({ providerId: providerUUID });
  const integrationOptions = Array.from(
    new Set(integrations?.data?.map((i) => i.integration)),
  ).filter(Boolean);
  const selectedIntegrationId = integrationOptions[0];

  const dataStructure: Location[] = (locations || []).map((location) => ({
    id: location.id,
    uuid: location.uuid,
    providerId: location.providerId || '',
    name: location.name,
    street: location.street || '',
    city: location.city || '',
    zipCode: location.zipCode || '',
    status: locationData?.find((loc) => loc.uuid === location.uuid)?.status || null,
    integrationStatus:
      integrations?.data?.find((integration) => integration.location_id === location.uuid)
        ?.status || 'none',
  }));

  const handleCopyToken = async (token: string) => {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(token);
      } else {
        const tempInput = document.createElement('input');
        tempInput.value = token;
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand('copy');
        document.body.removeChild(tempInput);
      }
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 600);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const handleDownload = () => {
    const data = dataStructure.map((location) => ({
      provider_name: currentProvider?.name || 'Unknown Provider',
      provider_uuid: providerUUID,
      location_name: location.name,
      location_uuid: location.uuid,
      location_address: `${location.street}, ${location.city}, ${location.zipCode}`,
      status: location.status || 'noStatus',
      integration_status: location.integrationStatus || 'none',
    }));
    downloadCSV({
      filename: `file_${currentProvider?.name || 'provider'}`,
      data,
      headers: {
        provider_name: 'Provider Name',
        provider_uuid: 'Provider UUID',
        location_name: 'Location name',
        location_uuid: 'Location UUID',
        location_address: 'Location address',
        status: 'Status',
        integration_status: 'Integration Status',
      },
    });
  };

  const tokenRefs = useRef<Array<HTMLDivElement | null>>([]);

  return (
    <Card className={classes.card}>
      <CardContent>
        <Typography variant="h5" className={classes.heading}>
          {t('usc.connect.ids')}
        </Typography>

        <div className={classes.containerRow}>
          <Typography variant="body2" className={classes.description}>
            {t('usc.connect.tokens.description')}
          </Typography>
          <Button
            onClick={handleDownload}
            variant="contained"
            size="small"
            color="primary"
            className={classes.defaultMedium}
          >
            {t('app.copy.all')}
          </Button>
        </div>

        <div>
          <h3>{t('app.provider.id')}</h3>
          <ul className={classes.tokenList}>
            <li className={classes.listItem}>
              <Typography className={classes.listItemTitle} variant="body2">
                {currentProvider?.name}
              </Typography>
              <div className={classes.token} ref={(ref) => (tokenRefs.current[0] = ref)}>
                <span data-testid="provider-uuid">{providerUUID}</span>
                <Tooltip
                  title={showCopied ? t('app.copied') : t('app.copy')}
                  enterDelay={70}
                  leaveDelay={10}
                >
                  <ContentCopy
                    className={classes.copyIcon}
                    onClick={() => handleCopyToken(providerUUID)}
                    fontSize="small"
                  />
                </Tooltip>
              </div>
            </li>
          </ul>

          <h3 className={classes.locationHeading}>{t('app.location.id')}</h3>
          <ul className={classes.tokenList}>
            {locations?.map((location, id) => (
              <LocationListItem
                key={location.id}
                location={location}
                locationData={locationData}
                integrations={integrations?.data}
                selectedIntegrationId={selectedIntegrationId}
                handleCopyToken={handleCopyToken}
                showCopied={showCopied}
                ref={(ref) => (tokenRefs.current[id + 1] = ref)}
              />
            ))}
            {locationsLoading ? (
              <li className={classes.listItem}>
                <Dots color={PRIMARY_BLUE} />
              </li>
            ) : locationsError ? (
              <li className={classes.listItem}>
                <Error error={locationsError} />
              </li>
            ) : null}
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};
